import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import { SHARED_COMPONENT_OVERRIDES } from './components';
import ChevronDownIcon from '~/components/Icons/ChevronDownIcon';
import { FIELD_CHEVRON, FIELD_SIZING } from './fieldSizing';
import {
    CSSObject,
    PaletteMode,
    PaletteOptions,
    Theme,
    ThemeOptions,
    alpha,
    darken,
    lighten,
} from '@mui/material';

export const PRIMARY_COLOR = '#00a5b9';
const PRIMARY_LIGHT = '#4dc0ce';
const PRIMARY_DARK = '#007382';
export const PRIMARY_CONTRAST_TEXT = '#001a1e';

export const SECONDARY_COLOR = '#f49800';
const SECONDARY_LIGHT = '#f6ad33';
const SECONDARY_DARK = '#c37a00';

const LIGHT_BACKGROUND = '#ffffff';
/** The fill of a control the user types or picks into, in the light theme. */
const LIGHT_SUNKEN_BACKGROUND = '#f5f7f8';
const TEXT_PRIMARY_LIGHT = '#2c2c2c';
const TEXT_SECONDARY_LIGHT = '#383838';
/** Text that only qualifies other text, such as a unit after a value. */
const TEXT_MUTED_LIGHT = '#6b7a83';

const DARK_BACKGROUND = '#1c262d';
/**
 * A dark surface raised above the background: panels, cards and the grid's
 * title and footer bars, so a card in the sidebar and the row footer beside
 * it read as one surface.
 */
export const DARK_RAISED_BACKGROUND = '#222d35';
/** The fill of a control the user types or picks into, in the dark theme. */
export const DARK_SUNKEN_BACKGROUND = '#151e24';
const TEXT_PRIMARY_DARK = '#fff';
const TEXT_SECONDARY_DARK = '#bbb';
const TEXT_MUTED_DARK = '#7e8f99';
const DARK_DIVIDER = '#333C42';
const TAG_CHIP_BORDER = '#2a363d';

/** The tint over a row the user is pointing at. */
export const ACTION_HOVER_OVERLAY = alpha(PRIMARY_COLOR, 0.05);
/** The tint over the choice in force. */
export const ACTION_SELECTED_OVERLAY = alpha(PRIMARY_COLOR, 0.1);
const WHITE_FOREGROUND = '#ffffff';
const DISABLED_FOREGROUND = '#666666';

const BORDER_RADIUS = 4;
/** Inset of a segmented control's segments within its track. */
const SEGMENT_TRACK_PADDING = 2;
/** A segment's radius, concentric with a track of {@link BORDER_RADIUS}. */
const SEGMENT_RADIUS = BORDER_RADIUS - SEGMENT_TRACK_PADDING;

const GLASS_OVERLAY = {
    background: 'rgba(4, 20, 32, 0.7)',
    backdropFilter: 'blur(3px)',
    WebkitBackdropFilter: 'blur(3px)',
};

/**
 * The light shadow of a surface that floats close over the viewer or the
 * app: toolbars, trays, popovers and a tool's options. An operation's panel
 * stands further off the views and takes the deeper `theme.shadows[8]`.
 */
export const FLOATING_SHADOW = '0 2px 6px rgba(0, 0, 0, 0.27)';

// The edge of a surface floating over the viewer or the app.
const FLOATING_EDGE = {
    borderRadius: `${BORDER_RADIUS}px`,
    boxShadow: FLOATING_SHADOW,
    border: '1px solid rgba(0, 0, 0, 0.1)',
};

const SCROLLBAR_THUMB = '#6b6b6b';
const SCROLLBAR_THUMB_ACTIVE = '#959595';
const SCROLLBAR_CORNER = '#2b2b2b';

const COURSE_STAGE_COLORS = {
    received: '#9aa5ac',
    imagePreparation: '#8ecfc9',
    imageRegistration: '#7ab8ff',
    oarContouring: '#6aa9ff',
    targetContouring: '#ff8f73',
    dosePrediction: '#c98aff',
    review: '#ffbe55',
    exported: '#5fd5e7',
} as const;

export type CourseStagePalette = Record<
    keyof typeof COURSE_STAGE_COLORS,
    string
>;
export type CourseStageColorKey = keyof CourseStagePalette;

const LIGHT_SEMANTIC_COLORS: PaletteOptions = {
    error: {
        main: '#d32f2f',
        light: '#ef5350',
        dark: '#c62828',
        contrastText: '#fff',
    },
    warning: {
        main: '#ed6c02',
        light: '#ff9800',
        dark: '#e65100',
        contrastText: '#fff',
    },
    info: {
        main: '#0288d1',
        light: '#03a9f4',
        dark: '#01579b',
        contrastText: '#fff',
    },
    success: {
        main: '#2e7d32',
        light: '#4caf50',
        dark: '#1b5e20',
        contrastText: '#fff',
    },
};

const DARK_SEMANTIC_COLORS: PaletteOptions = {
    error: {
        main: '#f44336',
        light: '#e57373',
        dark: '#d32f2f',
        contrastText: '#fff',
    },
    warning: {
        main: '#ffa726',
        light: '#ffb74d',
        dark: '#f57c00',
        contrastText: 'rgba(0, 0, 0, 0.87)',
    },
    info: {
        main: '#29b6f6',
        light: '#4fc3f7',
        dark: '#0288d1',
        contrastText: 'rgba(0, 0, 0, 0.87)',
    },
    success: {
        main: '#66bb6a',
        light: '#81c784',
        dark: '#388e3c',
        contrastText: 'rgba(0, 0, 0, 0.87)',
    },
};

const FONT_FAMILY_MONOSPACE = "'Roboto Mono', monospace";

const lightThemeTokens: PaletteOptions = {
    primary: {
        light: PRIMARY_LIGHT,
        main: PRIMARY_COLOR,
        dark: PRIMARY_DARK,
        contrastText: PRIMARY_CONTRAST_TEXT,
    },
    secondary: {
        light: SECONDARY_LIGHT,
        main: SECONDARY_COLOR,
        dark: SECONDARY_DARK,
        darker: darken(SECONDARY_DARK, 0.35),
        contrastText: '#000000',
    },
    ...LIGHT_SEMANTIC_COLORS,
    text: {
        primary: TEXT_PRIMARY_LIGHT,
        secondary: TEXT_SECONDARY_LIGHT,
        muted: TEXT_MUTED_LIGHT,
    },
    background: {
        paper: LIGHT_BACKGROUND,
        default: LIGHT_BACKGROUND,
        sunken: LIGHT_SUNKEN_BACKGROUND,
        raised: LIGHT_BACKGROUND,
    },
    action: {
        active: '#444444',
        hover: ACTION_HOVER_OVERLAY,
        selected: ACTION_SELECTED_OVERLAY,
        hoverNeutral: darken(LIGHT_BACKGROUND, 0.025),
        disabled: DISABLED_FOREGROUND,
    },
    courseStage: COURSE_STAGE_COLORS,
};

const darkThemeTokens: PaletteOptions = {
    primary: {
        light: PRIMARY_LIGHT,
        main: PRIMARY_COLOR,
        dark: PRIMARY_DARK,
        contrastText: PRIMARY_CONTRAST_TEXT,
    },
    secondary: {
        light: SECONDARY_LIGHT,
        main: SECONDARY_COLOR,
        dark: SECONDARY_DARK,
        darker: darken(SECONDARY_DARK, 0.35),
        contrastText: WHITE_FOREGROUND,
    },
    ...DARK_SEMANTIC_COLORS,
    background: {
        paper: DARK_BACKGROUND,
        default: DARK_BACKGROUND,
        sunken: DARK_SUNKEN_BACKGROUND,
        raised: DARK_RAISED_BACKGROUND,
    },
    text: {
        primary: TEXT_PRIMARY_DARK,
        secondary: TEXT_SECONDARY_DARK,
        muted: TEXT_MUTED_DARK,
    },
    divider: DARK_DIVIDER,
    action: {
        active: WHITE_FOREGROUND,
        hover: ACTION_HOVER_OVERLAY,
        selected: ACTION_SELECTED_OVERLAY,
        hoverNeutral: lighten(DARK_BACKGROUND, 0.025),
        disabled: DISABLED_FOREGROUND,
    },
    courseStage: COURSE_STAGE_COLORS,
};

/**
 * The tag chip (`<Chip variant="tag">`) on the sunken fill: a modality or
 * dataset type such as CT, a filter tag, or a structure with its swatch as
 * `icon`. `color="primary"` draws it in the primary colour over the
 * selected tint, `color="warning"` in the warning colour and tint.
 * `size="small"` is 18 px tall. Shared with the viewport grid's console
 * theme, so a chip reads the same on both.
 */
export const TAG_CHIP_VARIANTS: NonNullable<
    NonNullable<ThemeOptions['components']>['MuiChip']
>['variants'] = [
    {
        props: { variant: 'tag' },
        style: ({ theme }) => ({
            height: 'auto',
            fontWeight: 500,
            fontSize: 11,
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${TAG_CHIP_BORDER}`,
            backgroundColor: theme.palette.background.sunken,
            color: theme.palette.text.primary,
            '& .MuiChip-label': { padding: '2px 8px' },
            '& .MuiChip-icon': { marginLeft: 6, marginRight: -2 },
            '&.MuiChip-clickable:hover': {
                backgroundColor: theme.palette.action.hover,
            },
            '&.Mui-focusVisible': {
                outline: `1px solid ${theme.palette.primary.main}`,
            },
            '&.MuiChip-colorPrimary, &.MuiChip-colorPrimary:hover': {
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.action.selected,
            },
            '&.MuiChip-colorWarning, &.MuiChip-colorWarning:hover': {
                borderColor: theme.palette.warning.main,
                color: theme.palette.warning.main,
                backgroundColor: alpha(theme.palette.warning.main, 0.13),
            },
        }),
    },
    {
        props: { variant: 'tag', size: 'small' },
        style: {
            padding: 0,
            fontSize: 10,
            '& .MuiChip-label': { padding: '0 8px', lineHeight: '16px' },
        },
    },
];

// The segmented control's rules. Lengths are px strings so they read the same
// in `sx`, which scales a bare number by the theme's spacing or radius.

const segmentedTrack = (theme: Theme): CSSObject => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: `${BORDER_RADIUS}px`,
    backgroundColor: theme.palette.background.sunken,
    padding: `${SEGMENT_TRACK_PADDING}px`,
    gap: `${SEGMENT_TRACK_PADDING}px`,
});

/** A small segment, which makes its track as tall as a field. */
const SMALL_SEGMENT = { height: '26px', padding: '0 8px', fontSize: '12px' };

// A selected segment keeps its tint and accent label while disabled, at half
// opacity, so the chosen mode stays readable.
const segment = (theme: Theme): CSSObject => ({
    color: theme.palette.text.secondary,
    border: 'none',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
    backgroundColor: 'transparent',
    '&.Mui-disabled': {
        color: theme.palette.action.disabled,
        border: 'none',
    },
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        border: 'none',
    },
    '&.Mui-selected': {
        backgroundColor: alpha(theme.palette.primary.main, 0.16),
        color: theme.palette.primary.light,
        border: 'none',
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.24),
            border: 'none',
        },
        '&.Mui-disabled': {
            color: theme.palette.primary.light,
            opacity: 0.5,
        },
    },
});

/**
 * `Tabs` drawn as the small segmented control, keeping the tab roles and
 * arrow-key navigation that a toggle group does not have.
 */
export const segmentedTabsSx = (theme: Theme): CSSObject => ({
    ...segmentedTrack(theme),
    minHeight: 0,
    '& .MuiTabs-flexContainer': { gap: `${SEGMENT_TRACK_PADDING}px` },
    '& .MuiTabs-indicator': { display: 'none' },
    '& .MuiTab-root': {
        ...segment(theme),
        ...SMALL_SEGMENT,
        borderRadius: `${SEGMENT_RADIUS}px`,
        minHeight: 0,
        minWidth: 0,
    },
});

/**
 * The solid surface of a tool's options and of a panel they open: the page
 * background, a divider border and {@link FLOATING_SHADOW}.
 */
export const toolSurfaceSx = {
    bgcolor: 'background.default',
    backgroundImage: 'none',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    boxShadow: FLOATING_SHADOW,
} as const;

export const getDesignTokens = (mode: PaletteMode): ThemeOptions => ({
    components: {
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    workflowTitle: 'h2',
                    monoMeta: 'span',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    borderBottom: 'none',
                    boxShadow: 'none',
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:last-child td': {
                        borderBottom: 0,
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: ({ theme }) => ({
                    paddingTop: theme.spacing(0.5),
                    paddingBottom: theme.spacing(0.5),
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(1),
                    fontSize: 12,
                }),

                head: ({ theme }) => ({
                    backgroundColor: darken(
                        theme.palette.background.default,
                        0.1
                    ),
                }),
            },
        },

        MuiInput: {
            styleOverrides: {
                input: {
                    paddingTop: 0,
                },
            },
        },

        // Every bordered field (text, select, autocomplete, number) shares
        // FIELD_SIZING, so fields in a column line up. The sunken fill parts
        // a field from its surface, so hover and focus show on the 1 px
        // border alone. A disabled field drops the fill.
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({ theme }) => ({
                    // A field takes the width its row gives it, whatever it
                    // holds, and cuts long text short rather than widening.
                    minWidth: 0,
                    backgroundColor: theme.palette.background.sunken,
                    borderRadius: theme.shape.borderRadius,
                    paddingLeft: FIELD_SIZING.paddingX,
                    paddingRight: FIELD_SIZING.paddingX,
                    gap: FIELD_SIZING.gap,
                    fontSize: 13,
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    transition: theme.transitions.create(
                        ['border-color', 'background-color'],
                        { duration: theme.transitions.duration.short }
                    ),
                    '&:not(.MuiInputBase-multiline)': {
                        height: FIELD_SIZING.height,
                    },
                    '&.MuiInputBase-multiline': {
                        paddingTop: 8,
                        paddingBottom: 8,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.divider,
                        borderWidth: 1,
                        transition: theme.transitions.create('border-color', {
                            duration: theme.transitions.duration.short,
                        }),
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline':
                        {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 1,
                        },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.error.main,
                    },
                    '&.Mui-disabled': {
                        backgroundColor: 'transparent',
                    },
                }),
                // MUI's own input padding would stack on the field's.
                input: {
                    padding: 0,
                },
            },
        },

        MuiInputBase: {
            styleOverrides: {
                input: ({ theme }) => ({
                    '&::placeholder': {
                        color: theme.palette.text.secondary,
                        opacity: 1,
                    },
                }),
            },
        },

        // The field's gap places an adornment; its own margin would stack.
        MuiInputAdornment: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.text.secondary,
                }),
                positionStart: { marginRight: 0 },
                positionEnd: { marginLeft: 0 },
            },
        },

        MuiInputLabel: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.text.secondary,
                    fontSize: 14,
                    top: -2,
                    left: 0,
                    '&[data-shrink="false"]': {
                        left: -4,
                        top: -3,
                    },
                }),
            },
        },

        MuiSelect: {
            styleOverrides: {
                root: {
                    zIndex: 1900,
                },
                // The field pads the select; the select keeps room only for
                // the chevron, which sits the field's padding from the edge.
                select: {
                    '&&&': {
                        padding: 0,
                        paddingRight:
                            FIELD_CHEVRON.glyphWidth + FIELD_SIZING.gap,
                        minHeight: 0,
                    },
                },
                // The chevron turns over while the menu is open.
                icon: ({ theme }) => ({
                    right: FIELD_SIZING.paddingX - FIELD_CHEVRON.glyphInset,
                    top: `calc(50% - ${FIELD_CHEVRON.size / 2}px)`,
                    color: theme.palette.text.secondary,
                    transition: theme.transitions.create('transform', {
                        duration: theme.transitions.duration.shorter,
                    }),
                }),
            },
            defaultProps: {
                IconComponent: ChevronDownIcon,
                MenuProps: {
                    sx: {
                        '& .MuiMenu-paper': {
                            marginTop: '4px',
                        },
                        '& .MuiMenuItem-root': {
                            paddingLeft: '8px !important',
                        },
                    },
                },
            },
        },

        MuiAutocomplete: {
            defaultProps: {
                popupIcon: <ChevronDownIcon />,
            },
            styleOverrides: {
                popper: {
                    zIndex: 1600,
                    '& .MuiPaper-root': {
                        marginTop: '4px',
                    },
                },
                listbox: {
                    paddingTop: 0,
                    paddingBottom: 0,
                },
                // The field pads the input; the chevron sits the field's
                // padding from the edge, as a select's does.
                root: {
                    '&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root': {
                        paddingRight:
                            FIELD_SIZING.paddingX +
                            FIELD_CHEVRON.glyphWidth +
                            FIELD_SIZING.gap,
                    },
                },
                inputRoot: {
                    '&.MuiOutlinedInput-root, &.MuiOutlinedInput-root.MuiInputBase-sizeSmall':
                        {
                            paddingTop: 0,
                            paddingBottom: 0,
                            paddingLeft: FIELD_SIZING.paddingX,
                            '& .MuiAutocomplete-input': {
                                padding: 0,
                            },
                        },
                },
                endAdornment: {
                    right: FIELD_SIZING.paddingX - FIELD_CHEVRON.glyphInset,
                    top: `calc(50% - ${FIELD_CHEVRON.size / 2}px)`,
                },
                popupIndicator: ({ theme }) => ({
                    padding: 0,
                    color: theme.palette.text.secondary,
                }),
            },
        },

        ...SHARED_COMPONENT_OVERRIDES,

        MuiModal: {
            styleOverrides: {
                root: {
                    zIndex: 1501,
                },
            },
        },

        MuiToolbar: {
            styleOverrides: {
                root: ({ theme }) => ({
                    minHeight: 56,
                    paddingLeft: theme.spacing(0),
                    paddingRight: theme.spacing(0),
                    [theme.breakpoints.up('sm')]: {
                        paddingLeft: theme.spacing(0),
                        paddingRight: theme.spacing(0),
                    },
                }),
            },
        },
        MuiSlider: {
            styleOverrides: {
                root: {
                    padding: 0,
                    '@media (pointer: coarse)': {
                        padding: 0,
                    },
                },
                rail: {
                    borderRadius: 4,
                },
                track: {
                    height: 3,
                    borderRadius: 4,
                },
                thumb: ({ theme }) => ({
                    height: 12,
                    width: 12,
                    background: theme.palette.background.default,
                    border: `2px solid ${theme.palette.primary.main}`,
                    '&:hover, &.Mui-active, &.Mui-focusVisible': {
                        boxShadow: 'inherit',
                    },
                }),
            },
        },
        MuiCollapse: {
            styleOverrides: {
                root: {
                    '.MuiTreeItem-group': {
                        marginLeft: 8,
                    },
                },
            },
        },
        // The segmented control: a sunken track of borderless segments, each
        // rounded concentric with the track's field radius. A small group is
        // as tall as a field (32 px).
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: ({ theme }) => segmentedTrack(theme),
                firstButton: { borderRadius: SEGMENT_RADIUS },
                middleButton: {
                    borderRadius: SEGMENT_RADIUS,
                    margin: 0,
                    border: 0,
                },
                lastButton: {
                    borderRadius: SEGMENT_RADIUS,
                    margin: 0,
                    border: 0,
                    '&.Mui-disabled': { border: 0 },
                },
            },
        },

        MuiToggleButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                    ...segment(theme),
                }),

                sizeSmall: SMALL_SEGMENT,
                sizeMedium: {
                    padding: '4px 8px',
                    height: '36px',
                },
            },
        },

        MuiList: {
            styleOverrides: {
                root: ({ theme }) => ({
                    paddingTop: 0,
                    paddingBottom: 0,
                    '& li': {
                        '&::marker': {
                            color: theme.palette.primary.main,
                        },
                    },
                }),
            },
        },

        MuiBreadcrumbs: {
            styleOverrides: {
                ol: {
                    alignItems: 'center',
                    gap: 0,
                },
                li: {
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                },
                separator: ({ theme }) => ({
                    marginLeft: theme.spacing(0.5),
                    marginRight: theme.spacing(0.5),
                }),
            },
        },

        MuiCssBaseline: {
            styleOverrides: {
                '::selection': {
                    backgroundColor: PRIMARY_COLOR,
                    color: WHITE_FOREGROUND,
                },
                '::-webkit-selection': {
                    backgroundColor: PRIMARY_COLOR,
                    color: WHITE_FOREGROUND,
                },
                body: {
                    WebkitFontSmoothing: 'subpixel-antialiased',
                    MozOsxFontSmoothing: 'subpixel-antialiased',
                    '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
                        backgroundColor: 'transparent',
                        width: 5,
                        height: 6,
                    },
                    '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb':
                        {
                            backgroundColor: SCROLLBAR_THUMB,
                            minHeight: 36,
                            width: 1,
                        },
                    '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus':
                        {
                            backgroundColor: SCROLLBAR_THUMB_ACTIVE,
                        },
                    '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active':
                        {
                            backgroundColor: SCROLLBAR_THUMB_ACTIVE,
                        },
                    '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover':
                        {
                            backgroundColor: SCROLLBAR_THUMB_ACTIVE,
                        },
                    '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner':
                        {
                            backgroundColor: SCROLLBAR_CORNER,
                        },
                },
            },
        },
        MuiAccordion: {
            defaultProps: {
                disableGutters: true,
                square: true,
                elevation: 0,
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    '&::before': {
                        display: 'none',
                    },
                }),
            },
        },
        MuiAccordionSummary: {
            defaultProps: {
                expandIcon: <ArrowForwardIosSharpIcon sx={{ fontSize: 14 }} />,
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    border: 0,
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, .05)'
                            : 'rgba(0, 0, 0, .03)',
                    flexDirection: 'row-reverse',
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                        transform: 'rotate(90deg)',
                    },
                    '& .MuiAccordionSummary-content': {
                        marginLeft: theme.spacing(2),
                    },
                }),
            },
        },
        MuiChip: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                }),
                sizeSmall: {
                    fontSize: '10px',
                    height: '20px',
                    padding: '0 4px',
                },
            },
            variants: TAG_CHIP_VARIANTS,
        },

        MuiCheckbox: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                    },
                }),
                sizeMedium: {
                    padding: '6px',
                    '& .MuiSvgIcon-root': {
                        fontSize: '12px',
                        width: '18px',
                        height: '18px',
                    },
                },
                sizeSmall: {
                    padding: '4px',
                    '& .MuiSvgIcon-root': {
                        fontSize: '12px',
                        width: '14px',
                        height: '14px',
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                    transition: theme.transitions.create(
                        ['border-color', 'background-color'],
                        {
                            duration: theme.transitions.duration.short,
                        }
                    ),
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-disabled': {
                        opacity: 1,
                        color: theme.palette.action.disabled,
                    },
                }),
            },
            variants: [
                {
                    props: { selected: true },
                    style: ({ theme }) => ({
                        backgroundColor: theme.palette.action.selected,
                        '&:hover': {
                            backgroundColor: theme.palette.action.selected,
                        },
                    }),
                },
                // A destructive action: muted at rest, red under the pointer.
                {
                    props: { color: 'error' },
                    style: ({ theme }) => ({
                        color: theme.palette.text.secondary,
                        '&:hover': {
                            color: theme.palette.error.main,
                            backgroundColor: alpha(
                                theme.palette.error.main,
                                0.08
                            ),
                        },
                    }),
                },
            ],
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    width: 24,
                    height: 14,
                    padding: 0,
                    display: 'flex',
                    '&:active': {
                        '& .MuiSwitch-thumb': {
                            width: 13,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked': {
                            transform: 'translateX(7px)',
                        },
                    },
                },
                switchBase: ({ theme }) => ({
                    padding: 2,
                    '&.Mui-checked': {
                        transform: 'translateX(10px)',
                        color: WHITE_FOREGROUND,
                        '& + .MuiSwitch-track': {
                            opacity: 1,
                            backgroundColor: theme.palette.primary.main,
                        },
                    },
                }),
                thumb: ({ theme }) => ({
                    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
                    width: 10,
                    height: 10,
                    borderRadius: 5,
                    transition: theme.transitions.create(['width'], {
                        duration: 200,
                    }),
                }),
                track: ({ theme }) => ({
                    borderRadius: 14 / 2,
                    opacity: 1,
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,.35)'
                            : 'rgba(0,0,0,.25)',
                    boxSizing: 'border-box',
                }),
            },
        },
        // A switch sits flush in its row, a fixed gap from its label. A
        // checkbox keeps MUI's offset, which aligns its box with the text
        // above it.
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    '&:has(> .MuiSwitch-root)': {
                        marginLeft: 0,
                        marginRight: 0,
                        gap: 8,
                        '& .MuiFormControlLabel-label': {
                            fontSize: 12,
                            fontWeight: 500,
                        },
                    },
                },
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                }),
            },
        },
    },
    typography: (theme) => ({
        h6: {
            fontSize: 19,
        },
        body2: {
            fontSize: 14,
            color: theme.text.secondary,
        },
        workflowTitle: {
            fontSize: 18,
            fontWeight: 500,
        },
        // Small mono meta figure (counts, dates, IDs beside a name).
        monoMeta: {
            fontFamily: FONT_FAMILY_MONOSPACE,
            fontSize: 11,
            color: theme.text.secondary,
            whiteSpace: 'nowrap',
        },
        fontFamily: [
            'Poppins',
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
        ].join(','),

        fontFamilyMonospace: FONT_FAMILY_MONOSPACE,
    }),
    palette: {
        mode,
        ...(mode === 'light' ? lightThemeTokens : darkThemeTokens),
    },
    shape: {
        borderRadius: BORDER_RADIUS,
    },
    mixins: {
        glassOverlay: GLASS_OVERLAY,
        // Toolbars, trays and popovers over the viewer.
        floatingSurface: { ...GLASS_OVERLAY, ...FLOATING_EDGE },
        // Modals, cards and menus over the app, frosted more heavily.
        frostedSurface: {
            background: 'rgba(4, 30, 52, 0.65)',
            backdropFilter: 'blur(5px)',
            WebkitBackdropFilter: 'blur(5px)',
            ...FLOATING_EDGE,
        },
        // Pressed state of an IconButton toggle, which has no `selected` prop;
        // pair it with `aria-pressed`.
        pressedIconButton: {
            backgroundColor: alpha(PRIMARY_COLOR, 0.15),
        },
        // A field that picks a mode or an operation rather than data: tinted
        // where a data field is sunken, so the two read apart in one row.
        tonalField: {
            '&.MuiOutlinedInput-root': {
                backgroundColor: ACTION_SELECTED_OVERLAY,
            },
        },
    },
});

declare module '@mui/material/styles' {
    interface PaletteColor {
        darker?: string;
    }

    interface SimplePaletteColorOptions {
        darker?: string;
    }

    interface Mixins {
        glassOverlay: import('react').CSSProperties;
        floatingSurface: import('react').CSSProperties;
        frostedSurface: import('react').CSSProperties;
        pressedIconButton: import('react').CSSProperties;
        tonalField: import('@mui/material/styles').CSSObject;
    }

    interface MixinsOptions {
        glassOverlay?: import('react').CSSProperties;
        floatingSurface?: import('react').CSSProperties;
        frostedSurface?: import('react').CSSProperties;
        pressedIconButton?: import('react').CSSProperties;
        tonalField?: import('@mui/material/styles').CSSObject;
    }

    interface TypographyVariants {
        workflowTitle: import('react').CSSProperties;
        monoMeta: import('react').CSSProperties;
        fontFamilyMonospace: string;
    }

    interface TypographyVariantsOptions {
        workflowTitle?: import('react').CSSProperties;
        monoMeta?: import('react').CSSProperties;
        fontFamilyMonospace?: string;
    }

    interface TypeBackground {
        sunken: string;
        raised: string;
    }

    interface TypeAction {
        hoverNeutral: string;
    }

    interface TypeText {
        muted: string;
    }

    interface Palette {
        courseStage: CourseStagePalette;
    }

    interface PaletteOptions {
        courseStage?: CourseStagePalette;
    }
}

declare module '@mui/material/IconButton' {
    interface IconButtonOwnProps {
        selected?: boolean;
    }
}

declare module '@mui/material/Chip' {
    interface ChipPropsVariantOverrides {
        tag: true;
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        workflowTitle: true;
        monoMeta: true;
    }
}
